import React, { useState } from "react";
import { X } from "lucide-react";
import { handleStatusUpdate } from "../../actions/OrderAction";
import { getStatusClass } from "../layout/StatusStyleHandling";
import { orderStatusOptions, paymentStatusOptions } from "../../constants/Common";
import { formatDate } from "../../utils/DateFormat";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { capitalizeWords } from "../../utils/TextFormat";
import StatusEditForm from "../layout/StatusEditorForm";
import { useSession } from "../layout/SessionContext";

const OrderDetailsDialogbox = ({
  selectedOrder,
  closeModal,
  updateOrderStatus,
  updatePaymentStatus,
}) => {
  const { hasPermission } = useSession();
  const [isEditingOrderStatus, setIsEditingOrderStatus] = useState(false);
  const [isEditingPaymentStatus, setIsEditingPaymentStatus] = useState(false);

  const handleSaveStatus = async (id, status, statusNote, type) => {
    if (selectedOrder._id !== id) {
      toast.error("Error in selected Order");
      return;
    }
    try {
      const result = await handleStatusUpdate({
        id,
        type,
        newStatus: status,
        note: statusNote,
      });
      if (result.success) {
        if (type === "order") {
          updateOrderStatus(id, status);
          setIsEditingOrderStatus(false);
        } else {
          updatePaymentStatus(id, status);
          setIsEditingPaymentStatus(false);
        }
        toast.success(result.message || `${type} status updated`);
      } else {
        toast.error(result.message || `${type} status update failed`);
      }
    } catch (error) {
      toast.error(`Something went wrong while updating ${type} status. Please try again.`);
    }
  };

  if (!selectedOrder) {
    toast.error("Order not found");
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div
        className="bg-white rounded-[20px] max-h-[90vh] p-4 md:p-6 relative flex flex-col w-[90vw] md:w-[800px]"
        style={{ maxWidth: "800px", width: "90vw" }}
      >
        <button
          onClick={closeModal}
          className="absolute top-2 right-2 md:-top-5 md:-right-10 bg-white rounded-full p-2 shadow-md"
        >
          <X className="w-6 h-6 text-gray-600 hover:text-gray-800" />
        </button>

        <div>
          <h2 className="text-2xl md:text-3xl font-bold mb-3 text-text1">Order Details</h2>
          <p className="text-xs md:text-sm text-gray-500 font-normal">
            Status: <span className={getStatusClass(selectedOrder.orderStatus)}>
              {capitalizeWords(selectedOrder.orderStatus)}
            </span>
          </p>
        </div>
        <hr className="mt-4" />

        <div
          className="overflow-y-auto flex-1 pr-2"
          style={{ msOverflowStyle: "none", scrollbarWidth: "none" }}
        >
          <style jsx>{`
            div::-webkit-scrollbar {
              display: none;
            }
          `}</style>

          <div className="mb-3 mt-6">
            {[
              { label: "ID", value: selectedOrder.uid },
              { label: "Date & Time", value: selectedOrder.createdAt },
              {
                label: "Customer number",
                value: selectedOrder.customer?.mobileNumber
                  ? `+${selectedOrder.customer.mobileNumber}`
                  : "N/A",
              },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between py-3">
                <p className="font-normal md:text-base text-[14px] text-text2">{label}</p>
                <p className="font-normal md:text-base text-[14px] text-text1 text-right">
                  {label === "Date & Time" ? formatDate(value) : value || "N/A"}
                </p>
              </div>
            ))}

            {isEditingOrderStatus ? (
              <StatusEditForm
                type="Order"
                currentStatus={selectedOrder.orderStatus}
                setEditing={setIsEditingOrderStatus}
                handleSaveStatus={(id, status, note) => handleSaveStatus(id, status, note, "order")}
                statuses={orderStatusOptions}
                Id={selectedOrder._id}
              />
            ) : (
              selectedOrder.orderStatus && (
                <div className="flex justify-between py-3">
                  <p className="font-normal md:text-base text-[14px] text-text2">
                    Order status
                    {hasPermission("Orders", "edit") && (
                      <button
                        onClick={() => setIsEditingOrderStatus(true)}
                        className="ml-2 text-[#4B5563] text-sm bg-[#E5E7EB] px-3 py-0.5 rounded-xl"
                      >
                        Change
                      </button>
                    )}
                  </p>
                  <div className={getStatusClass(selectedOrder.orderStatus)}>
                    {capitalizeWords(selectedOrder.orderStatus)}
                  </div>
                </div>
              )
            )}
          </div>

          <hr />

          <div>
            <div className="flex justify-between pt-3 pb-2.5">
              <p className="md:font-medium font-bold text-base text-text1">Product Details</p>
            </div>
            {[
              {
                label: "Deliver to",
                value: selectedOrder.type==="LOCAL" ?
                (selectedOrder.deliveredToEmail || "N/A") :
                  (`+${selectedOrder.destinationNumber}` || "N/A")
              },
              {
                label: "Product name",
                value: selectedOrder.product?.productName
              },
              {
                label: "Actual price",
                value: `€ ${selectedOrder.product?.faceValue?.toFixed(2) || "0.00"}`
              },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between py-3">
             <p className="font-normal md:text-base text-[14px] text-text2 whitespace-nowrap shrink-0">{label}</p>
    <p className="font-normal md:text-base text-[14px] text-text1 text-right break-words min-w-0">
      {value || "N/A"}
    </p>
              </div>
            ))}
          </div>
          <hr className="mt-3" />

          <div>
            <div className="flex justify-between pt-3 pb-2.5">
              <p className="md:font-medium font-bold text-base  text-text1">Payment Details</p>
            </div>

            {[
              {
                label: "Total amount",
                value: `€ ${selectedOrder.total?.totalAmount?.toFixed(2) || "0.00"}`
              },
              { label: "Payment method", value: selectedOrder.paymentMethod }
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between py-3">
                <p className="font-normal md:text-base text-[14px] text-text2">{label}</p>
                <p className="font-normal md:text-base text-[14px] text-text1 text-right">
                  {value || "N/A"}
                </p>
              </div>
            ))}

            {isEditingPaymentStatus ? (
              <StatusEditForm
                type="Payment"
                currentStatus={selectedOrder.paymentStatus}
                setEditing={setIsEditingPaymentStatus}
                statuses={paymentStatusOptions}
                Id={selectedOrder._id}
                handleSaveStatus={(id, status, note) =>
                  handleSaveStatus(id, status, note, "payment")
                }
              />
            ) : (
              selectedOrder.paymentStatus && (
                <div className="flex justify-between py-3">
                  <p className="font-normal md:text-base text-[14px] text-text2">
                    Payment status
                    {hasPermission("Orders", "edit") && (
                      <button
                        onClick={() => setIsEditingPaymentStatus(true)}
                        className="ml-2 text-[#4B5563] text-sm bg-[#E5E7EB] px-3 py-0.5 rounded-xl"
                      >
                        Change
                      </button>
                    )}
                  </p>
                  <div className={getStatusClass(selectedOrder.paymentStatus)}>
                    {capitalizeWords(selectedOrder.paymentStatus)}
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsDialogbox;